import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaUser,
    FaSearch,
    FaFilter,
    FaCheckCircle,
    FaHourglassHalf,
    FaTimesCircle,
    FaChartLine,
    FaEye,
    FaUserTie,
    FaMapMarkerAlt,
    FaEnvelope,
    FaPhone,
    FaCalendar,
    FaBuilding,
    FaMoneyBillWave
} from "react-icons/fa";
import { toast } from "react-toastify";

const UserAdsTracking = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [userTypeFilter, setUserTypeFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 50;

    useEffect(() => {
        fetchUserTracking();
    }, [userTypeFilter]);

    const fetchUserTracking = async () => {
        setLoading(true);
        try {
            const params = {};
            if (userTypeFilter !== "all") {
                params.userType = userTypeFilter;
            }
            if (search.trim()) {
                params.search = search.trim();
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/admin/get/user-ads-tracking`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            setUsers(response.data.data || []);
        } catch (error) {
            console.error("Error fetching user tracking:", error);
            toast.error("Failed to fetch user tracking data");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchUserTracking();
    };

    const handleViewDetails = (userId) => {
        navigate(`/admin/user-ad-details/${userId}`);
    };

    // Filter users based on search
    const filteredUsers = users.filter(user => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            user.name?.toLowerCase().includes(q) ||
            user.iinsafId?.toLowerCase().includes(q) ||
            user.email?.toLowerCase().includes(q)
        );
    });

    // Pagination
    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    return (
        <div className="min-h-screen bg-slate-50/50 p-3 sm:p-6 lg:p-10">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-8">
                <div className="bg-gradient-to-br from-indigo-700 via-purple-800 to-pink-900 rounded-[2.5rem] p-6 sm:p-14 text-white shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-48 -mt-48" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-2xl -ml-32 -mb-32" />

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 sm:p-3 bg-white/15 backdrop-blur-md rounded-2xl shadow-inner border border-white/10">
                                <FaChartLine className="text-xl sm:text-2xl text-indigo-200" />
                            </div>
                            <span className="font-black text-indigo-100 uppercase tracking-[0.3em] text-[10px] sm:text-xs opacity-80">
                                User Performance Analytics
                            </span>
                        </div>
                        <h1 className="text-3xl sm:text-6xl font-black tracking-tighter mb-4 uppercase">
                            Ads Tracking
                        </h1>
                        <p className="text-indigo-100 max-w-2xl text-sm sm:text-xl font-medium opacity-70 leading-relaxed">
                            Monitor user activity, track ad completion rates, earnings, and analyze performance metrics across all reporters and influencers.
                        </p>
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="max-w-7xl mx-auto mb-8">
                <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl shadow-gray-200/40 border border-white p-3 md:p-6">
                    <div className="flex flex-col md:flex-row gap-3">
                        <div className="flex-1 relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-indigo-500 text-gray-400">
                                <FaSearch className="w-4 h-4" />
                            </div>
                            <input
                                type="text"
                                className="w-full pl-11 pr-4 py-3.5 md:py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500/50 focus:bg-white transition-all outline-none text-sm md:text-base font-bold shadow-inner"
                                placeholder="Search by name, IINSAF ID, or email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                            />
                        </div>
                        <select
                            value={userTypeFilter}
                            onChange={(e) => setUserTypeFilter(e.target.value)}
                            className="px-6 py-3.5 md:py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500/50 focus:bg-white transition-all outline-none text-sm md:text-base font-bold shadow-inner"
                        >
                            <option value="all">All Users</option>
                            <option value="reporter">Reporters Only</option>
                            <option value="influencer">Influencers Only</option>
                        </select>
                        <button
                            onClick={handleSearch}
                            className="h-[54px] md:h-auto px-10 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl font-black uppercase tracking-[0.15em] text-xs hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center justify-center gap-3 shadow-lg active:scale-95"
                        >
                            <FaFilter className="w-3 h-3" />
                            <span>Apply</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="max-w-7xl mx-auto mb-8">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                <FaUser className="text-blue-600" />
                            </div>
                            <div className="text-2xl font-black text-gray-800">{filteredUsers.length}</div>
                        </div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Users</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                                <FaCheckCircle className="text-green-600" />
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {filteredUsers.reduce((sum, u) => sum + u.stats.completed, 0)}
                            </div>
                        </div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Completed</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-yellow-50 rounded-xl flex items-center justify-center">
                                <FaHourglassHalf className="text-yellow-600" />
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {filteredUsers.reduce((sum, u) => sum + u.stats.accepted, 0)}
                            </div>
                        </div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                                <FaTimesCircle className="text-red-600" />
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {filteredUsers.reduce((sum, u) => sum + u.stats.rejected, 0)}
                            </div>
                        </div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Rejected</div>
                    </div>
                    <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 shadow-lg border border-violet-200">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <FaMoneyBillWave className="text-white" />
                            </div>
                            <div className="text-2xl font-black text-white">
                                ₹{filteredUsers.reduce((sum, u) => sum + (u.stats.walletBalance || 0), 0).toLocaleString()}
                            </div>
                        </div>
                        <div className="text-xs font-bold text-violet-100 uppercase tracking-widest">Wallet Balance</div>
                    </div>
                </div>
            </div>

            {/* User Table */}
            <div className="max-w-7xl mx-auto">
                {loading ? (
                    <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-lg">
                        <div className="relative">
                            <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                        </div>
                    </div>
                ) : paginatedUsers.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-16 text-center">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FaUser className="w-12 h-12 text-gray-300" />
                        </div>
                        <h3 className="text-2xl font-black text-gray-800 mb-3 uppercase tracking-tight">
                            No Users Found
                        </h3>
                        <p className="text-gray-500 text-lg font-medium max-w-md mx-auto">
                            No users match your search criteria. Try adjusting your filters.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Desktop Table */}
                        <div className="hidden md:block bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
                                        <tr>
                                            <th className="px-6 py-4 text-center text-xs font-black uppercase tracking-widest">S.No</th>
                                            <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest">User Info</th>
                                            <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-widest">Contact</th>
                                            <th className="px-6 py-4 text-center text-xs font-black uppercase tracking-widest">Total Ads</th>
                                            <th className="px-6 py-4 text-center text-xs font-black uppercase tracking-widest">Completed</th>
                                            <th className="px-6 py-4 text-center text-xs font-black uppercase tracking-widest">Wallet</th>
                                            <th className="px-6 py-4 text-center text-xs font-black uppercase tracking-widest">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {paginatedUsers.map((user, idx) => (
                                            <tr key={user.userId} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-xs font-black text-gray-400">#{(currentPage - 1) * rowsPerPage + (idx + 1)}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${user.role === "Reporter" ? "bg-blue-50" : "bg-purple-50"
                                                            }`}>
                                                            {user.role === "Reporter" ? (
                                                                <FaUser className="text-blue-600" />
                                                            ) : (
                                                                <FaUserTie className="text-purple-600" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-black text-gray-800">{user.name}</div>
                                                            <div className="text-xs font-bold text-gray-500">{user.iinsafId}</div>
                                                            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${user.role === "Reporter"
                                                                ? "bg-blue-100 text-blue-700"
                                                                : "bg-purple-100 text-purple-700"
                                                                }`}>
                                                                {user.role}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2 text-xs text-gray-600">
                                                            <FaEnvelope className="text-gray-400" />
                                                            <span className="font-medium truncate max-w-[200px]">{user.email}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-gray-600">
                                                            <FaPhone className="text-gray-400" />
                                                            <span className="font-medium">{user.mobile}</span>
                                                        </div>
                                                        {user.city && (
                                                            <div className="flex items-center gap-2 text-xs text-gray-600">
                                                                <FaMapMarkerAlt className="text-gray-400" />
                                                                <span className="font-medium">{user.city}, {user.state}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="text-2xl font-black text-gray-800">{user.stats.totalAds}</div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 rounded-xl">
                                                        <span className="text-lg font-black text-green-600">{user.stats.completed}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="inline-flex items-center justify-center px-4 py-2 bg-violet-50 rounded-xl border border-violet-200">
                                                        <span className="text-sm font-black text-violet-700">₹{(user.stats.walletBalance || 0).toLocaleString()}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button
                                                        onClick={() => handleViewDetails(user.userId)}
                                                        className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg active:scale-95"
                                                    >
                                                        <FaEye className="inline mr-2" />
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile Cards */}
                        <div className="md:hidden space-y-4">
                            {paginatedUsers.map((user) => (
                                <div key={user.userId} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${user.role === "Reporter" ? "bg-blue-50" : "bg-purple-50"
                                                }`}>
                                                {user.role === "Reporter" ? (
                                                    <FaUser className="text-blue-600" />
                                                ) : (
                                                    <FaUserTie className="text-purple-600" />
                                                )}
                                            </div>
                                            <div>
                                                <div className="text-sm font-black text-gray-800">{user.name}</div>
                                                <div className="text-xs font-bold text-gray-500">{user.iinsafId}</div>
                                            </div>
                                        </div>
                                        <span className={`px-2 py-1 rounded-full text-[9px] font-black uppercase ${user.role === "Reporter"
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-purple-100 text-purple-700"
                                            }`}>
                                            {user.role}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                                            <div className="text-lg font-black text-gray-800">{user.stats.totalAds}</div>
                                            <div className="text-[9px] font-bold text-gray-400 uppercase">Total</div>
                                        </div>
                                        <div className="bg-green-50 rounded-xl p-3 text-center">
                                            <div className="text-lg font-black text-green-600">{user.stats.completed}</div>
                                            <div className="text-[9px] font-bold text-green-600 uppercase">Done</div>
                                        </div>
                                        <div className="bg-violet-50 rounded-xl p-3 text-center">
                                            <div className="text-sm font-black text-violet-700">₹{(user.stats.walletBalance || 0).toLocaleString()}</div>
                                            <div className="text-[9px] font-bold text-violet-600 uppercase">Wallet</div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handleViewDetails(user.userId)}
                                        className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg active:scale-95"
                                    >
                                        <FaEye className="inline mr-2" />
                                        View Full Details
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                        <nav className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-lg p-2 rounded-2xl border border-gray-200 shadow-sm">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="w-12 h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-md disabled:opacity-30 transition-all active:scale-90"
                            >
                                ←
                            </button>
                            <div className="flex items-center gap-1 px-2">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    const pageNum = i + 1;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-12 h-12 rounded-xl text-sm font-black transition-all ${currentPage === pageNum
                                                ? 'bg-indigo-600 text-white shadow-lg'
                                                : 'text-gray-600 hover:bg-white'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="w-12 h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-md disabled:opacity-30 transition-all active:scale-90"
                            >
                                →
                            </button>
                        </nav>
                    </div>
                )}
            </div>
        </div >
    );
};

export default UserAdsTracking;
